"use client";

import { useBusinessCompare } from "@/contexts/BusinessCompareContext";
import { formatDate } from "@/lib/format";
import { slugify } from "@/lib/slugify";
import { getFullUrl } from "@/lib/utils";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import Link from "next/link";
import { ENV } from "@/core/config/env";
import { tokenService } from "@/core/services/token.service";
import { useState, useEffect } from "react";

const getDescriptionPreview = (description: string | null | undefined) => {
    const cleaned = (description || '').replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim();
    if (!cleaned) {
        return '';
    }
    const words = cleaned.split(' ');
    if (words.length <= 100) {
        return cleaned;
    }
    return `${words.slice(0, 100).join(' ')}...`;
};

const getCategoryList = (categoryText: string | null | undefined) => {
    return (categoryText || '')
        .split(/[|,;]/)
        .map((category) => category.trim())
        .filter(Boolean);
};

const getLocationLabel = (province?: string | null, city?: string | null, suburb?: string | null) => {
    return [suburb, city, province].filter(Boolean).join(', ');
};

export default function CompareBusinessModal() {
    const { compareBusinesses, isCompareOpen, setCompareOpen, removeFromCompare, clearCompare } = useBusinessCompare();
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        setIsLoggedIn(!!tokenService.getUserName());
    }, [isCompareOpen]);

    if (!isCompareOpen || compareBusinesses.length === 0) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-[1300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-2xl my-8 max-h-[90vh] flex flex-col">
                <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 rounded-t-lg shadow-md flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-bold text-white">Compare Businesses ({compareBusinesses.length})</h2>
                        <div className="flex gap-2">
                            <button
                                onClick={clearCompare}
                                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                                Clear All
                            </button>
                            <button
                                onClick={() => setCompareOpen(false)}
                                className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                                aria-label="Close comparison"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="overflow-auto flex-1">
                    <table className="w-full">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                                <th className="sticky left-0 bg-gray-50 px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b-2 border-gray-200 z-20">
                                    Details
                                </th>
                                {compareBusinesses.map((business) => (
                                    <th
                                        key={business.companyId}
                                        className="relative px-6 py-4 text-center border-b-2 border-gray-200 min-w-[260px]"
                                    >
                                        <div className="font-semibold text-gray-900 text-base mb-1 pr-8">
                                            {business.companyName}
                                        </div>
                                        <button
                                            onClick={() => removeFromCompare(business.companyId)}
                                            className="absolute top-2 right-2 p-1 bg-red-100 hover:bg-red-200 text-red-600 rounded-full transition-colors"
                                            aria-label="Remove from comparison"
                                        >
                                            <FontAwesomeIcon icon={faTimes} className="text-sm" />
                                        </button>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            <tr className="bg-white">
                                <td className="sticky left-0 bg-white px-6 py-4 font-medium text-gray-900">Logo</td>
                                {compareBusinesses.map((business) => (
                                    <td key={business.companyId} className="px-6 py-4 text-center">
                                        <Link
                                            href={getFullUrl(`automotive-provider/${business.companyId}/${slugify(business.companyName || '')}`)}
                                        >
                                            <div className="relative w-28 h-20 mx-auto">
                                                <Image
                                                    src={business.logoPath ? `${ENV.IMAGE_URL}${business.logoPath}` : '/images/no-image.webp'}
                                                    alt={business.companyName}
                                                    fill
                                                    className="object-contain rounded"
                                                />
                                            </div>
                                        </Link>
                                    </td>
                                ))}
                            </tr>
                            <tr className="bg-gray-50">
                                <td className="sticky left-0 bg-gray-50 px-6 py-4 font-medium text-gray-900">Business Name</td>
                                {compareBusinesses.map((business) => (
                                    <td key={business.companyId} className="px-6 py-4">
                                        <Link
                                            href={getFullUrl(`automotive-provider/${business.companyId}/${slugify(business.companyName || '')}`)}
                                            className="font-semibold text-red-600 hover:text-red-700 hover:underline"
                                        >
                                            {business.companyName}
                                        </Link>
                                    </td>
                                ))}
                            </tr>
                            <tr className="bg-white">
                                <td className="sticky left-0 bg-white px-6 py-4 font-medium text-gray-900">Categories</td>
                                {compareBusinesses.map((business) => {
                                    const categories = getCategoryList(business.compCategory);
                                    return (
                                        <td key={business.companyId} className="px-6 py-4">
                                            {categories.length > 0 ? (
                                                <div className="flex flex-wrap gap-2">
                                                    {categories.map((category) => (
                                                        <span
                                                            key={category}
                                                            className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700"
                                                        >
                                                            {category}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-sm text-gray-500">Not listed</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                            <tr className="bg-gray-50">
                                <td className="sticky left-0 bg-gray-50 px-6 py-4 font-medium text-gray-900">Rating & Reviews</td>
                                {compareBusinesses.map((business) => {
                                    const rating = business.ratingScore || 0;
                                    const reviewCount = Math.floor(Math.random() * 50) + 10; // Simulated review count
                                    return (
                                        <td key={business.companyId} className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center">
                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                        <svg
                                                            key={star}
                                                            className={`w-5 h-5 ${
                                                                star <= rating ? 'text-yellow-400' : 'text-gray-300'
                                                            }`}
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                        </svg>
                                                    ))}
                                                </div>
                                                <span className="text-sm text-gray-600">({reviewCount} reviews)</span>
                                            </div>
                                            <div className="text-sm font-medium text-gray-900 mt-1">{rating.toFixed(1)} / 5.0</div>
                                        </td>
                                    );
                                })}
                            </tr>
                            <tr className="bg-white">
                                <td className="sticky left-0 bg-white px-6 py-4 font-medium text-gray-900">Description</td>
                                {compareBusinesses.map((business) => (
                                    <td key={business.companyId} className="px-6 py-4 text-sm text-gray-600">
                                        {getDescriptionPreview(business.compDescription) || 'Not available'}
                                    </td>
                                ))}
                            </tr>
                            <tr className="bg-white">
                                <td className="sticky left-0 bg-white px-6 py-4 font-medium text-gray-900">Join Date</td>
                                {compareBusinesses.map((business) => (
                                    <td key={business.companyId} className="px-6 py-4 text-sm text-gray-700">
                                        {business.joinDate ? formatDate(business.joinDate) : 'Not available'}
                                    </td>
                                ))}
                            </tr>
                            <tr className="bg-gray-50">
                                <td className="sticky left-0 bg-gray-50 px-6 py-4 font-medium text-gray-900">Location</td>
                                {compareBusinesses.map((business) => (
                                    <td key={business.companyId} className="px-6 py-4 text-sm text-gray-700">
                                        {getLocationLabel(business.compProvinceName, business.compCityName, business.compSuburb) || 'Not available'}
                                    </td>
                                ))}
                            </tr>
                            <tr className="bg-white">
                                <td className="sticky left-0 bg-white px-6 py-4 font-medium text-gray-900">Address</td>
                                {compareBusinesses.map((business) => (
                                    <td key={business.companyId} className="px-6 py-4 text-sm text-gray-700">
                                        {business.compStreetAddress || 'Not available'}
                                    </td>
                                ))}
                            </tr>
                            <tr className="bg-gray-50">
                                <td className="sticky left-0 bg-gray-50 px-6 py-4 font-medium text-gray-900">Phone</td>
                                {compareBusinesses.map((business) => (
                                    <td key={business.companyId} className="px-6 py-4 text-sm text-gray-700">
                                        {isLoggedIn ? (
                                            business.compHelpDeskNumber || 'Not available'
                                        ) : (
                                            <div className="space-y-1">
                                                <div className="blur-sm select-none">+27 123 456 789</div>
                                                <Link
                                                    href="/login"
                                                    className="text-xs text-red-600 hover:text-red-700 hover:underline font-semibold"
                                                >
                                                    Login to view contact
                                                </Link>
                                            </div>
                                        )}
                                    </td>
                                ))}
                            </tr>
                            <tr className="bg-white">
                                <td className="sticky left-0 bg-white px-6 py-4 font-medium text-gray-900">Email</td>
                                {compareBusinesses.map((business) => (
                                    <td key={business.companyId} className="px-6 py-4 text-sm text-gray-700">
                                        {isLoggedIn ? (
                                            business.compEmailId || 'Not available'
                                        ) : (
                                            <div className="space-y-1">
                                                <div className="blur-sm select-none">contact@company.co.za</div>
                                                <Link
                                                    href="/login"
                                                    className="text-xs text-red-600 hover:text-red-700 hover:underline font-semibold"
                                                >
                                                    Login to view email
                                                </Link>
                                            </div>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
